import { Table, Tag, Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import ViewButton from "../UI/ViewButton/viewbutton";
import { useNavigate } from "react-router";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { UseMutateAsyncFunction } from "react-query";
import { AxiosResponse } from "axios";

type Props = {
  paginatedWarehouseInventories: any;
  isWarehouseInventoriesLoading: boolean;
  setPaginatedWarehouseInventories: Dispatch<any>;
  WarehouseInventoriesMutation: UseMutateAsyncFunction<
    AxiosResponse<any, any>,
    unknown,
    {
      warehouseid: string | number | undefined;
      limit: number;
      q: string;
      isActive?: boolean | undefined;
    },
    unknown
  >;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  filter: boolean | undefined;
  search: string;
  id: string | undefined;
};

interface StockInterfaces {
  key: string;
  name: string;
  code: string;
  isActive: boolean;
  supplier: string;
  category: string;
  quantity: number;
  min: String;
}

export const StockList = ({
  paginatedWarehouseInventories,
  isWarehouseInventoriesLoading,
  setPaginatedWarehouseInventories,
  WarehouseInventoriesMutation,
  page,
  search,
  filter,
  setPage,
  id,
}: Props) => {
  const [t] = useTranslation("common");
  const navigate = useNavigate();
  const [prevPage, setPrevPage] = useState<number>(0);
  const [maxPage, setMaxPage] = useState<number>(0);

  useEffect(() => {
    page > prevPage &&
      maxPage < page &&
      WarehouseInventoriesMutation({
        limit: page * 10 + 10,
        q: search,
        isActive: filter,
        warehouseid: id,
      }).then((res) => {
        if (page > maxPage) setMaxPage(page);
        setPaginatedWarehouseInventories(res.data?.data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filter]);

  const columns: ColumnsType<StockInterfaces> = [
    {
      title: `${t("name")}`,
      dataIndex: "item",
      key: "item",
      width: "25rem",
      className: "table-header",
      render: (item: any) => item.name,
    },
    {
      title: `${t("category")}`,
      dataIndex: "item",
      key: "category",
      width: "25rem",
      render: (item: any) => item.category.name,
    },
    {
      title: `${t("barcode")}`,
      dataIndex: "item",
      key: "barcode",
      render: (item: any) => item.barcode,
      width: "10rem",
    },
    {
      title: `${t("Supplier")}`,
      dataIndex: "item",
      key: "supplier",
      render: (item: any) => (item ? item.name : null),
    },
    {
      title: `${t("qty")}`,
      dataIndex: "quantity",
      key: "quantity",
      width: "4rem",
    },
    {
      title: `${t("min-qty")}`,
      dataIndex: "item",
      width: "5rem",
      key: "minInStock",
      render: (item: any) => item.minInStock,
    },
    {
      title: `${t("status")}`,
      dataIndex: "isActive",
      key: "isActive",
      width: "5rem",
      align: "center",
      render: (record: any) =>
        record ? (
          <Tag className="active">Active</Tag>
        ) : (
          <Tag className="inactive">Inactive</Tag>
        ),
    },

    {
      title: `${t("action")}`,
      key: "action",
      width: "5rem",
      fixed: "right",
      align: "center",
      render: (record: any) => (
        <Space size="middle">
          <ViewButton
            onClick={() =>
              navigate(`/inventory/summary/details/${record.id}`, {
                state: {
                  q: record.item.name,
                },
              })
            }
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        loading={isWarehouseInventoriesLoading}
        dataSource={paginatedWarehouseInventories}
        rowClassName={(record, index) =>
          index % 2 === 0
            ? "table-row-light table-row"
            : "table-row-dark table-row"
        }
        onChange={(selectedPage) => {
          setPrevPage(page);
          setPage(selectedPage?.current ?? 1);
        }}
        rowKey={(record: any) => record.id}
      />
    </>
  );
};
